import { Select as AntSelect, SelectProps } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { InputLayout } from "../layouts";
import { DownOutlined } from "@ant-design/icons";

interface Props extends Omit<SelectProps, "onSearch"> {
  label?: string;
  rules?: { required?: boolean }[];
  loadOptions?: () => DefaultOptionType[] | Promise<DefaultOptionType[]>;
  onSearch?: (
    value: string,
  ) => DefaultOptionType[] | Promise<DefaultOptionType[]>;
}

const Select = ({
  label,
  style,
  className,
  loadOptions,
  filterOption,
  onSearch,
  showSearch,
  options: propsOptions,
  rules,
  ...props
}: Props) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [asyncOptions, setAsyncOptions] = useState<DefaultOptionType[]>([]);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState({ width: 0, x: 0, below: 4, above: 4 });
  const options = propsOptions ?? asyncOptions;
  const isRemoteSearch = typeof onSearch === "function";

  useLayoutEffect(() => {
    const host = hostRef.current;
    const field = host?.closest(".group") as HTMLElement | null;
    const select = host?.querySelector(".ant-select") as HTMLElement | null;
    if (!field || !select) return;
    const measure = () => {
      const fieldBox = field.getBoundingClientRect();
      const selectBox = select.getBoundingClientRect();
      const gap = 4;
      setPopup({
        width: Math.round(fieldBox.width),
        x: Math.round(fieldBox.left - selectBox.left),
        below: Math.round(fieldBox.bottom - selectBox.bottom + gap),
        above: Math.round(selectBox.top - fieldBox.top + gap),
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(field);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    if (!loadOptions) return;
    handleOptions(loadOptions());
  }, []);

  const handleOptions = async (cb: any) => {
    setLoading(true);
    try {
      setAsyncOptions((await cb) ?? []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <InputLayout
      label={label}
      className={twMerge("[&>div:last-child]:px-0", className)}
      style={style}
      rules={rules}
    >
      <div ref={hostRef} className="w-full">
      <AntSelect
        {...props}
        className="w-full"
        popupMatchSelectWidth={popup.width || true}
        builtinPlacements={{
          bottomLeft: {
            points: ["tl", "bl"],
            offset: [popup.x, popup.below],
            overflow: { adjustX: 0, adjustY: 1 },
            dynamicInset: true,
          },
          bottomRight: {
            points: ["tr", "br"],
            offset: [-popup.x, popup.below],
            overflow: { adjustX: 0, adjustY: 1 },
            dynamicInset: true,
          },
          topLeft: {
            points: ["bl", "tl"],
            offset: [popup.x, -popup.above],
            overflow: { adjustX: 0, adjustY: 1 },
            dynamicInset: true,
          },
          topRight: {
            points: ["br", "tr"],
            offset: [-popup.x, -popup.above],
            overflow: { adjustX: 0, adjustY: 1 },
            dynamicInset: true,
          },
        }}
        loading={loading}
        options={options}
        virtual={false}
        showSearch={showSearch ?? isRemoteSearch}
        optionFilterProp="label"
        filterOption={
          isRemoteSearch
            ? false
            : (filterOption ??
              ((input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())))
        }
        onSearch={
          isRemoteSearch
            ? (value) => {
                handleOptions(onSearch(value));
              }
            : undefined
        }
        notFoundContent={options?.length ? "No match" : "No options"}
        getPopupContainer={() => document.body}
        dropdownStyle={{ zIndex: 2200 }}
        suffixIcon={
          <DownOutlined className="text-[1.4rem] text-black/40" />
        }
      />
      </div>
    </InputLayout>
  );
};

export default Select;
